import {defineStore} from 'pinia'
import {useRouter} from 'vue-router'
import {ref, watch, type Ref} from 'vue'
import {useStorage} from '@vueuse/core'
import {Breakpoint, useResponsive} from '../../composables/useResponsive'
import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'
import {QuickNavigationItemName} from '@toolify/client/src/modules/navigation/enum/QuickNavigationItemName'
import {LocalStorageKeyType} from '@toolify/client/src/types/LocalStorageKeyType'
import {isStackableOverlayNavigation, isStackableRoute} from '@toolify/client/src/setup/router/hooks/isStackableOverlayNavigation'
import {useRouteStore} from '@toolify/client/src/stores/RouteStore/useRouteStore'
import {ConversationType} from '@toolify/server/src/models/mongoose/MessageModel/enum/ConversationType'
import {IConversationTypeInterfacesMap} from '@toolify/server/src/services/MessageService/types/IConversationTypeInterfacesMap'

export interface ISelectedAgentPanelConversation {
  conversationType: ConversationType
  conversationPayload: IConversationTypeInterfacesMap[ConversationType]['conversationTypePayload']
  conversationKey: string
}

export type CurrentDirectChatPayload =
  | {type: 'create'}
  | {
    type: 'directConversation',
    directConversationId: string,
    index: number,
    preview?: CurrentDirectChatPreview
  }

export interface CurrentDirectChatPreview {
  id: string
  name: string
  avatarEntities: IResponseEntity[]
  thumbnailCount: number
  totalMemberCount: number
}

const quickNavigationItemValues = new Set<string>(Object.values(QuickNavigationItemName))

const selectedAgentPanelConversationSerializer = {
  read(value: string): ISelectedAgentPanelConversation | null {
    try {
      return JSON.parse(value) as ISelectedAgentPanelConversation | null
    } catch {
      return null
    }
  },
  write(value: ISelectedAgentPanelConversation | null): string {
    return JSON.stringify(value)
  },
}

function normalizeStoredQuickNavigationItem(activeQuickNavigationItem: Ref<QuickNavigationItemName | null>) {
  const value = activeQuickNavigationItem.value
  if (value === QuickNavigationItemName.Search || value === QuickNavigationItemName.Collections) {
    activeQuickNavigationItem.value = QuickNavigationItemName.Favourites
    return
  }
  if (value !== null && !quickNavigationItemValues.has(value)) {
    activeQuickNavigationItem.value = null
  }
}

export const useAppLayoutStore = defineStore('appLayout', () => {
  const isSideNavigationOpen = useStorage(LocalStorageKeyType.SideNavigationOpen, true)
  const isSettingsSidebarOpen = ref<boolean>(false)
  const preventNextMobileSidebarClose = ref<boolean>(false)
  const currentDirectChatPayload = ref<CurrentDirectChatPayload | null>(null)
  const currentDirectChatTransitionDirection = ref<'up' | 'down' | 'none'>('none')
  const activeQuickNavigationItem = useStorage<QuickNavigationItemName | null>(
    LocalStorageKeyType.ActiveQuickNavigationItem,
    QuickNavigationItemName.Favourites,
  )
  normalizeStoredQuickNavigationItem(activeQuickNavigationItem)
  const isSearchOverlayOpen = ref(false)
  const searchInputFocusNonce = ref(0)
  const isAgentPanelOpen = useStorage(LocalStorageKeyType.AgentPanelOpen, true)
  const isAgentHistoryPaneOpen = useStorage(LocalStorageKeyType.AgentPanelHistoryOpen, true)
  const selectedAgentPanelConversation = useStorage<ISelectedAgentPanelConversation | null>(
    LocalStorageKeyType.AgentPanelSelectedThread,
    null,
    undefined,
    {serializer: selectedAgentPanelConversationSerializer},
  )
  const selectedDirectConversationId = useStorage(LocalStorageKeyType.AgentPanelConversationId, '')
  if (!selectedAgentPanelConversation.value && selectedDirectConversationId.value) {
    selectedAgentPanelConversation.value = {
      conversationType: ConversationType.Direct,
      conversationPayload: {directConversationId: selectedDirectConversationId.value},
      conversationKey: selectedDirectConversationId.value,
    }
  }
  const router = useRouter()
  const routeStore = useRouteStore()
  const {isSmallerThan, isSmallerThanLarge} = useResponsive()

  if (isSmallerThanLarge.value) {
    isSideNavigationOpen.value = true
  }

  let lastDesktopSideNavOpen = isSmallerThanLarge.value ? true : isSideNavigationOpen.value
  watch(isSmallerThanLarge, (isMobile, wasMobile) => {
    if (isMobile && !wasMobile) {
      lastDesktopSideNavOpen = isSideNavigationOpen.value
      isSideNavigationOpen.value = false
      isSettingsSidebarOpen.value = false
    } else if (!isMobile && wasMobile) {
      isSideNavigationOpen.value = lastDesktopSideNavOpen
    }
  })

  router.beforeEach((to, from) => {
    if (isSmallerThan(Breakpoint.Large).value) {
      if (preventNextMobileSidebarClose.value) {
        return
      }
      if ((from?.matched?.length ?? 0) === 0) {
        return
      }
      if (isStackableOverlayNavigation(to, from)) {
        return
      }
      if (isStackableRoute(from) && to.fullPath === routeStore.lastNonStackableRoute?.fullPath) {
        return
      }
      isSideNavigationOpen.value = false
      isSettingsSidebarOpen.value = false
    }
  })

  router.afterEach(() => {
    preventNextMobileSidebarClose.value = false
  })

  function openDirectConversation(directConversationId: string, index: number, preview?: CurrentDirectChatPreview) {
    const previousIndex = currentDirectChatPayload.value?.type === 'directConversation'
      ? currentDirectChatPayload.value.index
      : null
    const delta = previousIndex === null ? 0 : index - previousIndex

    currentDirectChatTransitionDirection.value = delta > 0 ? 'down' : delta < 0 ? 'up' : 'none'
    currentDirectChatPayload.value = {
      type: 'directConversation',
      directConversationId,
      index,
      preview,
    }
  }

  function openCreateDirectConversation() {
    currentDirectChatTransitionDirection.value = 'none'
    currentDirectChatPayload.value = {
      type: 'create',
    }
  }

  function closeDirectChat() {
    currentDirectChatTransitionDirection.value = 'none'
    currentDirectChatPayload.value = null
  }

  function navigateToQuickItem(name: QuickNavigationItemName) {
    if (activeQuickNavigationItem.value === name) {
      return
    }
    activeQuickNavigationItem.value = name
    if (!isSideNavigationOpen.value) {
      isSideNavigationOpen.value = true
    }
  }

  function toggleSearchOverlay() {
    if (isSearchOverlayOpen.value) {
      isSearchOverlayOpen.value = false
      return
    }
    isSearchOverlayOpen.value = true
    searchInputFocusNonce.value++
  }

  function closeSearchOverlay() {
    isSearchOverlayOpen.value = false
  }

  return {
    isSideNavigationOpen,
    isSettingsSidebarOpen,
    preventNextMobileSidebarClose,
    currentDirectChatPayload,
    currentDirectChatTransitionDirection,
    openDirectConversation,
    openCreateDirectConversation,
    closeDirectChat,
    activeQuickNavigationItem,
    isSearchOverlayOpen,
    searchInputFocusNonce,
    isAgentPanelOpen,
    isAgentHistoryPaneOpen,
    selectedDirectConversationId,
    selectedAgentPanelConversation,
    navigateToQuickItem,
    toggleSearchOverlay,
    closeSearchOverlay,
  }
})
